import React, { useState } from 'react';
import CreatorOnboardingStepSpaceTitle from './creator_onboarding_step_space_title';
import CreatorOnboardingStepLandingPage from './creator_onboarding_step_landing_page';
import CreatorOnboardingStepAppTheme from './creator_onboarding_step_app_theme';
import CreatorOnboardingStepSplashScreen from './creator_onboarding_step_splash_screen';
import {
  useCreateSpaceMutation,
  useGetSpaceQuery,
  useUpdateSpaceMutation,
  useUpdateUserProfileMutation,
} from '../../app/smorgAppSyncGraphQLApi';
import { healthProGroupForSpaceID } from '../../services/users';
import { DEFAULT_SPACE_COMETCHAT_CHAT_CONFIG } from '../../services/chat';
import { applySmorgStudioSpaceWhiteLabelRules } from '../../services/spaces';
import { useGetUserProfileQuery } from '../../app/smorgAppSyncGraphQLApiCustomHooks';
import { activateSpaceOperation } from '../../operations/user_profile_operations';

const STEP_SPACE_TITLE_AND_DESCRIPTION = 'space-title-and-description';
const STEP_APP_THEME = 'app-theme';
const STEP_SPLASH_SCREEN = 'splash-screen';
const STEP_LANDING_PAGE = 'landing-page';
const STEPS = [
  STEP_SPACE_TITLE_AND_DESCRIPTION,
  STEP_APP_THEME,
  STEP_SPLASH_SCREEN,
  STEP_LANDING_PAGE,
];

const CreatorOnboardingWizard = () => {
  const { data: healthProSpace } = useGetSpaceQuery();

  const { data: userProfile } = useGetUserProfileQuery();

  const [createSpace] = useCreateSpaceMutation();
  const [updateSpace] = useUpdateSpaceMutation();
  const [updateUserProfile] = useUpdateUserProfileMutation();

  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const currentStep = STEPS[currentStepIndex];
  const [title, setTitle] = useState(healthProSpace?.title);
  const [shortDescription, setShortDescription] = useState(
    healthProSpace?.shortDescription,
  );
  const [whiteLabelElements, setWhiteLabelElements] = useState(
    healthProSpace?.whiteLabelElements || {},
  );
  const [landingPageContent, setLandingPageContent] = useState(
    healthProSpace?.landingPageContent || '',
  );
  // const [landingPageBackgroundColor, setLandingPageBackgroundColor] = useState(
  //   healthProSpace?.landingPageStyle?.backgroundColor || null,
  // );
  const landingPageBackgroundColor =
    healthProSpace?.landingPageStyle?.backgroundColor || null;
  const [coverImageUrl, setCoverImageUrl] = useState(
    healthProSpace?.landingPageCoverImageUrl,
  );

  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const createAndActivateSpace = async (spaceWithGroups) => {
    const createResult = await createSpace(spaceWithGroups);
    await activateSpaceOperation(createResult.data.id);
    return createResult;
  };

  const onNext = async () => {
    if (!title) {
      setErrorMessage('Please give your space a title');
      return;
    }
    if (currentStepIndex < STEPS.length - 1) {
      setCurrentStepIndex(currentStepIndex + 1);
    } else {
      setIsLoading(true);
      try {
        const updatedSpace = {
          ...(healthProSpace || {}),
          title,
          shortDescription,
          whiteLabelElements,
          landingPageContent,
        };
        const currentHealthProGroup =
          healthProSpace && healthProGroupForSpaceID(healthProSpace.id);
        const spaceWithGroups = {
          ...updatedSpace,
          chatConfig: DEFAULT_SPACE_COMETCHAT_CHAT_CONFIG,
          groups: currentHealthProGroup && [currentHealthProGroup],
        };
        const { data: createdSpace } = await (spaceWithGroups.id
          ? updateSpace(spaceWithGroups)
          : createAndActivateSpace(spaceWithGroups));
        const updatedUserProfile = {
          ...userProfile,
          showOnboarding: false,
        };
        await updateUserProfile(updatedUserProfile);
        // dispatch(initialLoadAction(createdSpace.id));
        applySmorgStudioSpaceWhiteLabelRules(createdSpace.whiteLabelElements);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const onBack = () => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(currentStepIndex - 1);
    }
  };

  if (currentStep === STEP_SPACE_TITLE_AND_DESCRIPTION) {
    return (
      <CreatorOnboardingStepSpaceTitle
        title={title}
        shortDescription={shortDescription}
        setTitle={(v) => {
          setTitle(v);
          setErrorMessage('');
        }}
        setShortDescription={(v) => {
          setShortDescription(v);
          setErrorMessage('');
        }}
        errorMessage={errorMessage}
        onNext={onNext}
        onBack={onBack}
        isLoading={isLoading}
      />
    );
  }
  if (currentStep === STEP_APP_THEME) {
    return (
      <CreatorOnboardingStepAppTheme
        whiteLabelElements={whiteLabelElements}
        setWhiteLabelElements={(v) => {
          setWhiteLabelElements(v);
          setErrorMessage('');
        }}
        errorMessage={errorMessage}
        onNext={onNext}
        onBack={onBack}
        isLoading={isLoading}
        spaceTitle={title}
      />
    );
  }
  if (currentStep === STEP_SPLASH_SCREEN) {
    return (
      <CreatorOnboardingStepSplashScreen
        whiteLabelElements={whiteLabelElements}
        setWhiteLabelElements={(v) => {
          setWhiteLabelElements(v);
          setErrorMessage('');
        }}
        errorMessage={errorMessage}
        onNext={onNext}
        onBack={onBack}
        isLoading={isLoading}
      />
    );
  }
  if (currentStep === STEP_LANDING_PAGE) {
    return (
      <CreatorOnboardingStepLandingPage
        whiteLabelElements={whiteLabelElements}
        landingPageContent={landingPageContent}
        setLandingPageContent={setLandingPageContent}
        coverImageUrl={coverImageUrl}
        setCoverImageUrl={setCoverImageUrl}
        errorMessage={errorMessage}
        onNext={onNext}
        onBack={onBack}
        isLoading={isLoading}
        landingPageBackgroundColor={landingPageBackgroundColor}
      />
    );
  }
  return null;
};

export default CreatorOnboardingWizard;
